<template>
    <div class="billing-payment-method expandable-form">
        <div v-if="showBorders" class="billing-payment-method__border"></div>
        <div v-if="showStoredBillingInfo" class="billing-payment-method-info">
            <div class="billing-payment-method__current">
                <span class="billing-payment-method__text billing-payment-method__title">
                    {{
                        isFormOpen ? pageAttr.edit_payment_method : pageAttr.payment_method_on_file
                    }}
                </span>
                <template v-if="!isFormOpen">
                    <span class="billing-payment-method__info">{{ paymentInfo }}</span>
                    <span
                        class="billing-payment-method__text billing-payment-method__copy"
                        v-if="isSwitchPlan && !isPaymentMethodEditSPEnabled">
                        {{ pageAttr.payment_method_copy }}
                    </span>
                </template>
            </div>
            <div class="billing-payment-method__container-btn-edit">
                <button
                    class="billing-payment-method__btn-edit"
                    data-testid="btn-edit-payment-method"
                    :aria-expanded="isFormOpen"
                    :disabled="isLoadingBillingInfo"
                    @click="handlePaymentMethodEditButton">
                    <div :class="iconCaretClassList"></div>
                    {{ btnEditPaymentMethodText }}
                </button>
            </div>
        </div>
        <div class="edit-payment-method-form" v-if="isFormOpen || showPaymentForm">
            <!-- Two-button experience: SPC (FF off) or PaymentMethodSelection (FF on) -->
            <SharedPaymentComponent
                v-if="twoButtonExperience && !intlSingleColumnPayment"
                :isEditPayment="true"
                :isFromPaymentSummary="true"
                :handleProcessedPaymentError="errorMessageHandler"
                :errorMessageElementId="errorMessageElementId"
                :pageTypeForTracking="pageTypeForTracking"
                @update-payment-method-success="setPaymentMethodEditFormEnabled"
                @update:activeTab="emitActiveTab($event)" />
            <PaymentMethodSelection
                v-else
                :activeTab="activeTab"
                @update:activeTab="emitActiveTab($event)"
                :isEditPayment="twoButtonExperience && intlSingleColumnPayment"
                :isFromPaymentSummary="true"
                :handleProcessedPaymentError="errorMessageHandler"
                @update-payment-method-success="setPaymentMethodEditFormEnabled"
                @update-active-tab="updateActiveTab"
                :handleProcessedPayment="handleProcessedPayment"
                :errorMessageElementId="errorMessageElementId"
                :isFTC="isFTC"
                ref="paymentFormParent"
                :pageTypeForTracking="pageTypeForTracking" />
        </div>
        <div v-if="showBorders" class="billing-payment-method__border"></div>
    </div>
</template>

<script>
    import { capitalize } from 'aa/vue/components/account/helpers';
    import { PAYMENT_PAYPAL } from 'aa/vue/constants/shared';
    import { mapState, mapGetters, mapMutations } from 'vuex';
    import { PAYMENT_METHOD_EDIT_SP } from 'aa/helpers/featureConstants';
    import SharedPaymentComponent from 'aa/vue/components/payment/SharedPaymentComponent';
    import { ACTION_NAMES, PAGE_TYPES } from 'services/Tracking';
    import { PAGE_TYPES as AA_PAGE_TYPES } from 'aa/vue/constants/aaTrackingConstants';
    import { handleProcessedPaymentError as editHandleError } from 'aa/vue/components/payment/editPaymentUtil';
    import { handleProcessedPaymentError as signupHandleError } from 'aa/vue/components/payment/signupUtil';
    import AccountPageTrackingService from 'aa/services/AccountPageTrackingService';
    import isEmpty from 'lodash/isEmpty';
    import PaymentMethodSelection from 'aa/vue/components/payment/PaymentMethodSelection';
    import { TAB } from 'aa/vue/FormConfig';
    import { SINGLE_COLUMN_PAYMENT_PAGE } from 'aa/helpers/featureConstants';

    export default {
        name: 'BillingPaymentMethod',
        data: function () {
            return {
                isFormOpen: false,
                activeTab: TAB.cc,
            };
        },
        components: {
            SharedPaymentComponent,
            PaymentMethodSelection,
        },
        props: {
            pageAttr: {
                type: Object,
            },
            isFTC: {
                type: Boolean,
                default: false,
            },
            handleProcessedPayment: {
                type: Function,
                default: () => {},
            },
            twoButtonExperience: {
                type: Boolean,
                default: false,
            },
            errorMessageElementId: {
                type: String,
                default: null,
            },
            pageTypeForTracking: {
                type: String,
                default: '',
            },
            accountBalanceCoversPurchase: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            ...mapState('user', {
                isExSubscriber: (state) => state.isExSubscriber,
            }),
            ...mapGetters(['featureIsActive']),
            ...mapGetters('plan', ['isSwitchPlan']),
            ...mapGetters('storedPaymentMethod', ['getBillingInfo', 'hasValidStoredPaymentMethod']),
            isExSubscriberWithValidPayment() {
                // ex-sub with no valid payment is set as "gift" in recurly
                return this.isExSubscriber && this.hasValidStoredPaymentMethod;
            },
            errorMessageHandler() {
                return this.isFormOpen && this.twoButtonExperience
                    ? editHandleError
                    : signupHandleError;
            },
            isCard() {
                return this.getBillingInfo ? this.getBillingInfo.isTypeCard : false;
            },
            isPaypal() {
                return this.getBillingInfo ? this.getBillingInfo.isTypePaypal : false;
            },
            isGift() {
                return this.getBillingInfo ? this.getBillingInfo.isTypeGift : false;
            },
            paymentInfo() {
                if (this.isCard) {
                    return `${this.getBillingInfo?.cardType?.toUpperCase()} *${this.getBillingInfo?.lastFour}`;
                } else if (this.isPaypal) {
                    return capitalize(PAYMENT_PAYPAL);
                } else if (this.isGift) {
                    return 'N/A';
                }
            },
            isLoadingBillingInfo() {
                return this.isExSubscriber && isEmpty(this.$store.state.serverData.billingInfo);
            },
            btnEditPaymentMethodText() {
                return this.isFormOpen
                    ? this.pageAttr.cancel_edit_payment_method
                    : this.pageAttr.edit_payment_method;
            },
            iconCaretClassList() {
                return ['icon', 'icon--caret', this.isFormOpen ? 'icon--caret--up' : ''];
            },
            isPaymentMethodEditSPEnabled() {
                return this.featureIsActive(PAYMENT_METHOD_EDIT_SP);
            },
            intlSingleColumnPayment() {
                return !!this.$store.state.serverData?.featureFlag?.[SINGLE_COLUMN_PAYMENT_PAGE];
            },
            trackingService() {
                if (!this.$trackingService) {
                    return new AccountPageTrackingService();
                }
            },
            showStoredBillingInfo() {
                // do not display if they have no stored payment method OR it is expired
                if (!this.hasValidStoredPaymentMethod) {
                    return false;
                }

                // Also supports legacy switch plan (INTL switch plan summary sans payment form)
                return (
                    (this.isSwitchPlan && this.isPaymentMethodEditSPEnabled) ||
                    this.hasValidStoredPaymentMethod
                );
            },
            showPaymentForm() {
                if (!this.hasValidStoredPaymentMethod && !this.accountBalanceCoversPurchase) {
                    return true;
                }

                return !this.isSwitchPlan && !this.isExSubscriberWithValidPayment;
            },
            showBorders() {
                return this.isSwitchPlan || this.isExSubscriberWithValidPayment;
            },
        },
        methods: {
            ...mapMutations('ftc', ['setEditingStoredPaymentMethod']),
            emitActiveTab(tab) {
                this.activeTab = tab;
                this.$emit('update:activeTab', tab);
            },
            handlePaymentMethodEditButton() {
                const trackingServiceInstance = this.$trackingService ?? this.trackingService;

                const trackingActionName = !this.isFormOpen
                    ? ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD
                    : ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_CANCEL;

                const trackingParams = {
                    ctaText: this.btnEditPaymentMethodText,
                    pageURL: window.location.href,
                    pageType: this.isExSubscriber
                        ? AA_PAGE_TYPES.EXSUB_PAYMENT
                        : PAGE_TYPES.PLAN_CHANGE_CONFIRMATION,
                };

                trackingServiceInstance.trackAction(trackingActionName, trackingParams);

                this.isFormOpen = !this.isFormOpen;
                this.setEditingStoredPaymentMethod(this.isFormOpen);
            },
            setPaymentMethodEditFormEnabled(event) {
                this.isFormOpen = !event;
            },
            updateActiveTab(activeTab) {
                this.$emit('update-active-tab', activeTab);
            },
        },
        watch: {
            isFormOpen(newValue) {
                if (!newValue && !this.showPaymentForm) {
                    this.$store.commit('payment/setFormReference', null);
                }
                this.$emit('is-payment-method-form-edit', newValue);
            },
        },
    };
</script>
